import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { PersonnelRolesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as CommitteeMemberRoleActions from "../../../actions/committeeMemberRole";
import * as NavigationActions from "../../../actions/navigation";
import * as PersonnelActions from "../../../actions/personnel";
import { InlineLoading } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import { PersonnelSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { TogglePageItem } from "../../../reducers/pages/togglePage";

import "./PersonnelRolesPage.scss";

interface PersonnelRolesPageProps {
    personnel: Dtos.Personnel;
    personnelRoleSummaries: Dtos.PersonnelRoleSummary[];
    loadingPersonnel: boolean;
    loadPersonnelSuccess: boolean;
    loadPersonnelFailure: boolean;
    loadPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    toggleItems: TogglePageItem[];
    committeeMemberRoles: Dtos.CommitteeMemberRole[];
    loadingCommitteeMemberRoles: boolean;
    loadCommitteeMemberRolesSuccess: boolean;
    loadCommitteeMemberRolesFailure: boolean;
    loadCommitteeMemberRolesErrorCode: string;
    clearPersonnel: PersonnelActions.IPersonnelClearActionCreator;
    loadPersonnel: PersonnelActions.IPersonnelLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearCommitteeMemberRoles: CommitteeMemberRoleActions.ICommitteeMemberRoleClearActionCreator;
    loadCommitteeMemberRoles: CommitteeMemberRoleActions.ICommitteeMemberRolesLoadByPersonnelIdActionCreator;
}

const PersonnelRolesPage: FC<PersonnelRolesPageProps> = ({
    personnel,
    personnelRoleSummaries,
    loadingPersonnel,
    loadPersonnelSuccess,
    loadPersonnelFailure,
    loadPersonnelErrorCode,
    permissions,
    committeeMemberRoles,
    loadingCommitteeMemberRoles,
    loadCommitteeMemberRolesSuccess,
    loadCommitteeMemberRolesFailure,
    loadCommitteeMemberRolesErrorCode,
    clearPersonnel,
    loadPersonnel,
    setBreadcrumbs,
    setDrawerItems,
    clearCommitteeMemberRoles,
    loadCommitteeMemberRoles
}) => {
    //
    // -- Params --
    const { personnelId } = useParams<{ personnelId: string }>();
    const personnelIdNumber = parseInt(personnelId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showRoles =
        !loadingPersonnel &&
        loadPersonnelSuccess &&
        !loadingCommitteeMemberRoles &&
        loadCommitteeMemberRolesSuccess &&
        !creatingTree;


    // Load personnel & roles
    useEffect(() => {
        if (personnelIdNumber && !personnel) {
            setDrawerItems(PersonnelSubMenu(personnelIdNumber, permissions));
            setBreadcrumbs(PersonnelRolesBreadcrumbs(personnelIdNumber));

            loadPersonnel(personnelIdNumber);
            loadCommitteeMemberRoles(personnelIdNumber);
        }
    }, [
        personnelIdNumber,
        permissions,
        personnel,
        setBreadcrumbs,
        setDrawerItems,
        loadPersonnel,
        PersonnelRolesBreadcrumbs,
        PersonnelSubMenu,
        loadCommitteeMemberRoles,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadPersonnelFailure) {
            navigate(`/error/${loadPersonnelErrorCode}`);
        }
        if (loadCommitteeMemberRolesFailure) {
            navigate(`/error/${loadCommitteeMemberRolesErrorCode}`);
        }
    }, [
        loadPersonnelFailure, 
        loadPersonnelErrorCode, 
        loadCommitteeMemberRolesFailure, 
        loadCommitteeMemberRolesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (personnelIdNumber) {
            setDrawerItems(
                PersonnelSubMenu(
                    personnelIdNumber,
                    permissions,
                    personnel?.fellowships
                )
            );
            if (personnel?.firstName && personnel?.lastName) {
                setBreadcrumbs(
                    PersonnelRolesBreadcrumbs(
                        personnelIdNumber,
                        `${personnel.firstName} ${personnel.lastName}`
                    )
                );
            }
        }
    }, [
        personnelIdNumber,
        personnel,
        permissions,
        PersonnelSubMenu,
        PersonnelRolesBreadcrumbs,
        setDrawerItems,
        setDrawerItems,
    ]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            clearPersonnel();
            clearCommitteeMemberRoles();
        };
    }, [clearPersonnel, clearCommitteeMemberRoles]);

    // Refresh tree
    useEffect(() => {
        if (personnelRoleSummaries || committeeMemberRoles) {
            setInitalizedTreeData(false);
        }
    }, [personnelRoleSummaries, committeeMemberRoles, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (personnelRoleSummaries && !initalizedTreeData) {
            setCreatingTree(true);

            // If no personnel role summaries return
            if (personnelRoleSummaries?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createCommitteeChildNodes = (
                roles: Dtos.CommitteeMemberRole[],
                showCeased: boolean
            ) => {
                let children = [];

                children = 
                    roles
                    ?.filter((x) => showCeased ? true : !x?.dateCeased)
                    ?.sort((a, b) => {
                        const committeeComparison = a?.committee?.name.localeCompare(b?.committee?.name) || 0;
                        if (committeeComparison !== 0) {
                            return committeeComparison;
                        }

                        const roleComparison = a?.committeeMemberRoleType?.value.localeCompare(b?.committeeMemberRoleType?.value) || 0
                        if (roleComparison !== 0) {
                            return roleComparison;
                        }

                        const dateA = a?.dateStarted ? new Date(a.dateStarted).getTime() : 0;
                        const dateB = b?.dateStarted ? new Date(b.dateStarted).getTime() : 0;
                        return dateB - dateA;
                    })
                    ?.map((x) => ({
                            id: `committeeMemberRole-${x?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.dateCeased ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.SHIELDHALVED} />
                                    </div>
                                    <div className="px-1">
                                        {x?.committee?.name}
                                    </div>
                                    <div className="px-1">
                                        {x?.committeeMemberRoleType?.value}
                                    </div>
                                    <div className="px-1">
                                        <span data-tip={convertToApproximateDateString(x?.dateStarted)}>
                                            {convertToShortDateString(x?.dateStarted)}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span data-tip={convertToApproximateDateString(x?.dateCeased)}>
                                            {convertToShortDateString(x?.dateCeased)}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Committee</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createGlobalChildNodes = (
                globalPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    globalPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createTrialChildNodes = (
                trialPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    trialPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const trialComparison =
                                a?.personnelRole?.trialDisplay?.localeCompare(
                                    b?.personnelRole?.trialDisplay
                                ) || 0;
                            if (trialComparison !== 0) {
                                return trialComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.trialDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Trial</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createInstitutionChildNodes = (
                institutionPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    institutionPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const institutionComparison =
                                a?.personnelRole?.institutionDisplay?.localeCompare(
                                    b?.personnelRole?.institutionDisplay
                                ) || 0;
                            if (institutionComparison !== 0) {
                                return institutionComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.institutionDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Institution</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createTrialInstitutionChildNodes = (
                trialInstitutionPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    trialInstitutionPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const trialComparison =
                                a?.personnelRole?.trialDisplay?.localeCompare(
                                    b?.personnelRole?.trialDisplay
                                ) || 0;
                            if (trialComparison !== 0) {
                                return trialComparison;
                            }
                            const institutionComparison =
                                a?.personnelRole?.institutionDisplay?.localeCompare(
                                    b?.personnelRole?.institutionDisplay
                                ) || 0;
                            if (institutionComparison !== 0) {
                                return institutionComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.trialDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.institutionDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "1fr 3fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Trial</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Institution</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createTrialInstitutionLaboratoryChildNodes = (
                trialInstitutionLaboratoryPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    trialInstitutionLaboratoryPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const trialComparison =
                                a?.personnelRole?.trialDisplay?.localeCompare(
                                    b?.personnelRole?.trialDisplay
                                ) || 0;
                            if (trialComparison !== 0) {
                                return trialComparison;
                            }
                            const institutionComparison =
                                a?.personnelRole?.institutionDisplay?.localeCompare(
                                    b?.personnelRole?.institutionDisplay
                                ) || 0;
                            if (institutionComparison !== 0) {
                                return institutionComparison;
                            }
                            const laboratoryComparison =
                                a?.personnelRole?.laboratoryDisplay?.localeCompare(
                                    b?.personnelRole?.laboratoryDisplay
                                ) || 0;
                            if (laboratoryComparison !== 0) {
                                return laboratoryComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr 2fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.trialDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.institutionDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.laboratoryDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "1fr 3fr 3fr 3fr 3fr 2fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Trial</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Institution</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Laboratory</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createTrialGroupChildNodes = (
                trialGroupPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    trialGroupPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const trialComparison =
                                a?.personnelRole?.trialDisplay?.localeCompare(
                                    b?.personnelRole?.trialDisplay
                                ) || 0;
                            if (trialComparison !== 0) {
                                return trialComparison;
                            }
                            const institutionComparison =
                                a?.personnelRole?.institutionDisplay?.localeCompare(
                                    b?.personnelRole?.institutionDisplay
                                ) || 0;
                            if (institutionComparison !== 0) {
                                return institutionComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.trialDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.groupDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "1fr 3fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Trial</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Group</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createGroupChildNodes = (
                groupPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    groupPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const groupComparison =
                                a?.personnelRole?.groupDisplay?.localeCompare(
                                    b?.personnelRole?.groupDisplay
                                ) || 0;
                            if (groupComparison !== 0) {
                                return groupComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.groupDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Group</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createTrialGroupLaboratoryChildNodes = (
                trialGroupLaboratoryPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    trialGroupLaboratoryPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const trialComparison =
                                a?.personnelRole?.trialDisplay?.localeCompare(
                                    b?.personnelRole?.trialDisplay
                                ) || 0;
                            if (trialComparison !== 0) {
                                return trialComparison;
                            }
                            const groupComparison =
                                a?.personnelRole?.groupDisplay?.localeCompare(
                                    b?.personnelRole?.groupDisplay
                                ) || 0;
                            if (groupComparison !== 0) {
                                return groupComparison;
                            }
                            const laboratoryComparison =
                                a?.personnelRole?.laboratoryDisplay?.localeCompare(
                                    b?.personnelRole?.laboratoryDisplay
                                ) || 0;
                            if (laboratoryComparison !== 0) {
                                return laboratoryComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateB - dateA;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr 2fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.trialDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.groupDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.laboratoryDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "1fr 3fr 3fr 3fr 3fr 2fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Trial</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Group</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Laboratory</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createLaboratoryChildNodes = (
                laboratoryPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];

                children =
                    laboratoryPersonnelRoleSummaries
                        ?.filter((x) =>
                            showCeased ? true : !x?.personnelRole?.dateTo
                        )
                        ?.sort((a, b) => {
                            const laboratoryComparison =
                                a?.personnelRole?.laboratoryDisplay?.localeCompare(
                                    b?.personnelRole?.laboratoryDisplay
                                ) || 0;
                            if (laboratoryComparison !== 0) {
                                return laboratoryComparison;
                            }
                            const roleComparison =
                                a?.personnelRole?.roleDisplay?.localeCompare(
                                    b?.personnelRole?.roleDisplay
                                ) || 0;
                            if (roleComparison !== 0) {
                                return roleComparison;
                            }

                            const dateA = a?.personnelRole?.dateStarted
                                ? new Date(
                                      a.personnelRole.dateStarted
                                  ).getTime()
                                : 0;
                            const dateB = b?.personnelRole?.dateStarted
                                ? new Date(
                                      b.personnelRole.dateStarted
                                  ).getTime()
                                : 0;

                            return dateA - dateB;
                        })
                        ?.map((x) => ({
                            id: `personnelrole-${x?.personnelRole?.id}`,
                            data: (
                                <div
                                    className={
                                        x?.personnelRole?.dateTo ? "ceased" : ""
                                    }
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SHIELDHALVED
                                            }
                                        />
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.laboratoryDisplay}
                                    </div>
                                    <div className="px-1">
                                        {x?.personnelRole?.roleDisplay}
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateFrom
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                    <div className="px-1">
                                        <span
                                            data-tip={convertToApproximateDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        >
                                            {convertToShortDateString(
                                                x?.personnelRole?.dateTo
                                            )}
                                        </span>
                                        <ReactTooltip />
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Laboratory</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Role</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            const createChildNodes = (
                typedPersonnelRoleSummaries: Dtos.PersonnelRoleSummary[],
                showCeased: boolean
            ) => {
                let children = [];
                typedPersonnelRoleSummaries.forEach((type) => {
                    switch (type.personnelRoleSummaryType) {
                        case Dtos.PersonnelRoleSummaryType.General:
                            children = createGlobalChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType.Trial:
                            children = createTrialChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType.Institution:
                            children = createInstitutionChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType.TrialInstitution:
                            children = createTrialInstitutionChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType
                            .TrialInstitutionLaboratory:
                            children =
                                createTrialInstitutionLaboratoryChildNodes(
                                    typedPersonnelRoleSummaries,
                                    showCeased
                                );
                            break;
                        case Dtos.PersonnelRoleSummaryType.Group:
                            children = createGroupChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType.TrialGroup:
                            children = createTrialGroupChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType.TrialGroupLaboratory:
                            children = createTrialGroupLaboratoryChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        case Dtos.PersonnelRoleSummaryType.Laboratory:
                            children = createLaboratoryChildNodes(
                                typedPersonnelRoleSummaries,
                                showCeased
                            );
                            break;
                        default:
                            children = [];
                            break;
                    }
                });

                return children;
            };

            let treeNodes =
                [...personnelRoleSummaries]
                    ?.filter((x) =>
                        showCeased
                            ? true
                            : x?.personnelRoleSummaries?.filter(
                                    (x) => !x?.personnelRole?.dateTo
                                )?.length !== 0
                    )
                    ?.sort(
                        (a, b) =>
                            a?.personnelRoleSummaryType -
                            b?.personnelRoleSummaryType
                    )
                    ?.map((x) => ({
                        id: `type-${x?.personnelRoleSummaryType}`,
                        data: (
                            <div className="d-flex align-items-center">
                                {getIconByType(x?.personnelRoleSummaryType)}
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "250px" }}
                                >
                                    {x?.display}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {
                                            x?.personnelRoleSummaries?.filter(
                                                (x) =>
                                                    showCeased
                                                        ? true
                                                        : !x?.personnelRole?.dateTo
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(
                                    x?.personnelRoleSummaries,
                                    showCeased
                                ),
                            ],
                        },
                    })) ?? [];

            let committeeNodes: any = null;
            if ((committeeMemberRoles ?? []).some((x) => showCeased || !x?.dateCeased)) {
                committeeNodes = {
                    id: `type-committee`,
                    data: (
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: "40px" }}
                            >
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.COMMITTEE} />
                            </div>
                            <span
                                className="pl-4 mb-0"
                                style={{ width: "250px" }}
                            >
                                Committee
                            </span>
                            <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ width: "50px" }}
                            >
                                <span className="badge badge-pill badge-secondary mb-0">
                                    {(committeeMemberRoles ?? []).filter(
                                        (y) => showCeased || !y?.dateCeased
                                    )?.length ?? 0}
                                </span>
                            </div>
                        </div>
                    ),
                    icon: null,
                    collapsible: true,
                    subTree: {
                        children: createCommitteeChildNodes(
                            committeeMemberRoles ?? [],
                            showCeased
                        ),
                    },
                };
            }

            // Initialize tree toggle
            // const expanded = treeNodes.map((z) => z.id);

            // setToggleState(expanded);
            setTreeData({ children: committeeNodes ? [...treeNodes, committeeNodes] : treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        personnelRoleSummaries,
        committeeMemberRoles,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
        showCeased,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id?.includes("type")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                } else if (id?.includes("personnelrole")) {
                    navigate(
                        `/personnel/${personnel?.id}/roles/${id?.replace("personnelrole-", "")}`
                    );
                } else if (id?.includes("committeeMemberRole")) {
                    const selectedId = +id?.replace("committeeMemberRole-", "");
                    const committeeMemberRole = committeeMemberRoles?.find(x => x.id == selectedId);
                    navigate(
                        `/committees/${committeeMemberRole?.committeeId}/members/${committeeMemberRole?.committeeMember?.id}/roles/${committeeMemberRole.id}`
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions, personnel, committeeMemberRoles]
    );

    const getIconByType = (type: Dtos.PersonnelRoleSummaryType) => {
        switch (type) {
            case Dtos.PersonnelRoleSummaryType.General:
                return (
                    <div
                        className="d-flex align-item-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <FontAwesomeIcon
                            icon={FontAwesomeIcons.Duotone.GLOBE}
                        />
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.Trial:
                return (
                    <div
                        className="d-flex align-item-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <FontAwesomeIcon
                            icon={FontAwesomeIcons.Duotone.TRIAL}
                        />
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.Institution:
                return (
                    <div
                        className="d-flex align-item-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <FontAwesomeIcon
                            icon={FontAwesomeIcons.Duotone.INSTITUTION}
                        />
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.TrialInstitution:
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <div className="large-small-icon">
                            <FontAwesomeIcon
                                className="large-icon"
                                icon={FontAwesomeIcons.Duotone.TRIAL}
                                fixedWidth
                            />
                            <FontAwesomeIcon
                                className="small-icon"
                                icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                fixedWidth
                            />
                        </div>
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.TrialInstitutionLaboratory:
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <div className="large-medium-icon">
                            <FontAwesomeIcon
                                className="large-icon"
                                icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                fixedWidth
                            />
                            <FontAwesomeIcon
                                className="small-icon"
                                icon={FontAwesomeIcons.Duotone.FLASKVIAL}
                                fixedWidth
                            />
                        </div>
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.Group:
                return (
                    <div
                        className="d-flex align-item-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <FontAwesomeIcon
                            icon={FontAwesomeIcons.Duotone.GROUP}
                        />
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.TrialGroup:
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <div className="large-small-icon">
                            <FontAwesomeIcon
                                className="large-icon"
                                icon={FontAwesomeIcons.Duotone.TRIAL}
                                fixedWidth
                            />
                            <FontAwesomeIcon
                                className="small-icon"
                                icon={FontAwesomeIcons.Duotone.GROUP}
                                fixedWidth
                            />
                        </div>
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.TrialGroupLaboratory:
                return (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <div className="large-medium-icon">
                            <FontAwesomeIcon
                                className="large-icon"
                                icon={FontAwesomeIcons.Duotone.GROUP}
                                fixedWidth
                            />
                            <FontAwesomeIcon
                                className="small-icon"
                                icon={FontAwesomeIcons.Duotone.FLASKVIAL}
                                fixedWidth
                            />
                        </div>
                    </div>
                );
            case Dtos.PersonnelRoleSummaryType.Laboratory:
                return (
                    <div
                        className="d-flex align-item-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <FontAwesomeIcon
                            icon={FontAwesomeIcons.Duotone.FLASKVIAL}
                        />
                    </div>
                );
            default:
                return (
                    <div
                        className="d-flex align-item-center justify-content-center"
                        style={{ width: "40px" }}
                    >
                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                    </div>
                );
        }
    };

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-auto mb-2">
                        <h1>Roles</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased roles"
                            labelB="Show ceased roles"
                            style={{ minWidth: "160px" }}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.PersonnelRoleCreate
                        ) &&
                            personnel &&
                            personnel?.id > 0 && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/personnel/${personnel?.id}/roles/create`}
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showRoles ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showRoles ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no roles to view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        showRoles,
        creatingTree,
        treeData,
        handleToggle,
        toggleState,
        showCeased,
        onChange,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.PersonnelRoleView}
            loading={loadingPersonnel || loadingCommitteeMemberRoles}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<PersonnelRolesPageProps> => {
    let personnel: Dtos.Personnel | undefined = !(
        state.personnel.formData instanceof Array
    )
        ? state.personnel.formData
        : undefined;
    let committeeMemberRoles: Dtos.CommitteeMemberRole[] | undefined =
        state.committeeMemberRole.formData instanceof Array
            ? state.committeeMemberRole.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        personnel: personnel!,
        personnelRoleSummaries: personnel
            ? personnel.personnelRoleSummaries
            : undefined,
        loadingPersonnel:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Pending,
        loadPersonnelSuccess:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Success,
        loadPersonnelFailure:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Failure,
        loadPersonnelErrorCode: state.personnel.loadState.errorCode,
        permissions: permissions,
        toggleItems: state.togglePage.items,
        committeeMemberRoles: committeeMemberRoles,
        loadingCommitteeMemberRoles:
            state.committeeMemberRole.loadState &&
            state.committeeMemberRole.loadState.status == RequestState.Pending,
        loadCommitteeMemberRolesSuccess:
            state.committeeMemberRole.loadState &&
            state.committeeMemberRole.loadState.status == RequestState.Success,
        loadCommitteeMemberRolesFailure:
            state.committeeMemberRole.loadState &&
            state.committeeMemberRole.loadState.status == RequestState.Failure,
        loadCommitteeMemberRolesErrorCode:
            state.committeeMemberRole.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<PersonnelRolesPageProps> => {
    return {
        loadPersonnel: bindActionCreators(PersonnelActions.LoadPersonnelById, dispatch),
        clearPersonnel: bindActionCreators(PersonnelActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadCommitteeMemberRoles: bindActionCreators(CommitteeMemberRoleActions.LoadCommitteeMemberRolesByPersonnelId, dispatch),
        clearCommitteeMemberRoles: bindActionCreators(CommitteeMemberRoleActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonnelRolesPage);
